import React, { useState } from "react";
import { X } from "lucide-react";

/**
 * Modal to mark a metric result as incorrect and provide the expected value.
 *
 * Props:
 * - visible (bool)
 * - onClose ()
 * - onSubmit ({ expected_value, explanation })
 * - metricName (string)
 * - resultType ("boolean" | "rating" | "numeric" | "enum")
 * - resultTypeConfig (object) — for enum: { values: [...] }
 * - measuredValue (any) — the current measured value
 */
const AnnotateMetricModal = ({
  visible,
  onClose,
  onSubmit,
  metricName,
  resultType,
  resultTypeConfig,
  measuredValue,
}) => {
  const [expectedValue, setExpectedValue] = useState("");
  const [explanation, setExplanation] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!visible) return null;

  const handleSubmit = async () => {
    if (!expectedValue && expectedValue !== 0) return;
    setSubmitting(true);
    try {
      await onSubmit({
        expected_value: { value: expectedValue },
        explanation,
      });
      onClose();
    } catch (err) {
      console.error("Failed to submit annotation:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const renderExpectedInput = () => {
    switch (resultType) {
      case "boolean":
        return (
          <div className="flex gap-2">
            {["pass", "fail"].map((val) => (
              <button
                key={val}
                onClick={() => setExpectedValue(val)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  expectedValue === val
                    ? val === "pass"
                      ? "bg-green-100 border-green-400 text-green-800"
                      : "bg-red-100 border-red-400 text-red-800"
                    : "bg-card border-input text-foreground hover:bg-muted/50"
                }`}
              >
                {val === "pass" ? "Pass" : "Fail"}
              </button>
            ))}
          </div>
        );

      case "rating":
        return (
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((val) => (
              <button
                key={val}
                onClick={() => setExpectedValue(val)}
                className={`w-10 h-10 rounded-lg text-sm font-semibold border transition-colors ${
                  expectedValue === val
                    ? "bg-indigo-100 border-indigo-400 text-indigo-800"
                    : "bg-card border-input text-foreground hover:bg-muted/50"
                }`}
              >
                {val}
              </button>
            ))}
          </div>
        );

      case "numeric":
        return (
          <input
            type="number"
            value={expectedValue}
            onChange={(e) => setExpectedValue(e.target.value ? Number(e.target.value) : "")}
            className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Enter expected numeric value"
          />
        );

      case "enum":
        const values = resultTypeConfig?.values || [];
        return (
          <div className="flex flex-wrap gap-2">
            {values.map((val) => (
              <button
                key={val}
                onClick={() => setExpectedValue(val)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                  expectedValue === val
                    ? "bg-violet-100 border-violet-400 text-violet-800"
                    : "bg-card border-input text-foreground hover:bg-muted/50"
                }`}
              >
                {val}
              </button>
            ))}
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={expectedValue}
            onChange={(e) => setExpectedValue(e.target.value)}
            className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Enter expected value"
          />
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-card rounded-xl shadow-2xl p-4 max-w-lg w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Mark as Incorrect</h3>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded">
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          The metric <span className="font-semibold">"{metricName}"</span> produced an incorrect result.
          {measuredValue != null && (
            <span> Current value: <span className="font-mono font-semibold">{String(measuredValue)}</span></span>
          )}
        </p>

        <div className="mb-4">
          <label className="block text-sm font-medium text-foreground mb-2">
            What should the correct value be?
          </label>
          {renderExpectedInput()}
        </div>

        <div className="mb-5">
          <label className="block text-sm font-medium text-foreground mb-1">
            Explanation (optional)
          </label>
          <textarea
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
            rows={3}
            placeholder="Why is this result incorrect? What should the metric look for?"
          />
        </div>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-foreground bg-muted rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={(!expectedValue && expectedValue !== 0) || submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Saving..." : "Mark as Incorrect"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnnotateMetricModal;
